import React, { useEffect, useState } from 'react'
import { CircleHelp } from 'lucide-react'
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'
import MenuGuideButton from '../components/MenuGuideButton'
import { api } from '../lib/api'
import { formatDate, formatDateTimeMinute } from '../lib/date'

const PERIOD_OPTIONS = [
    { value: 'TODAY', label: '오늘' },
    { value: 'THIS_MONTH', label: '이번달' },
    { value: 'LAST_7_DAYS', label: '최근 7일' },
    { value: 'LAST_30_DAYS', label: '최근 30일' },
    { value: 'LAST_90_DAYS', label: '최근 90일' },
    { value: 'PREVIOUS_MONTH', label: '전월' },
]
const METRIC_HELP = {
    estimatedMrr: '현재 유효한 개인 유료 구독의 월 환산액과 활성 조직계약의 월 계약액을 합산한 예상 월 반복매출입니다. 실제 수납·환불은 포함하지 않습니다.',
    estimatedArr: '예상 MRR에 12개월을 곱한 연간 반복매출 단순 환산값입니다. 실제 수납·환불은 포함하지 않습니다.',
    arpa: '예상 MRR을 활성 과금 계정 수로 나눈 평균 월 매출입니다. 활성 개인 유료 구독 수와 활성 조직계약 수를 기준으로 계산합니다.',
    collectedAmount: '선택 기간의 개인 결제 수납액과 조직 청구 수납액을 합산한 뒤 환불액을 뺀 순수납액입니다.',
    activeRevenueAccounts: '현재 활성 개인 유료 구독 수와 활성 조직계약 수의 합계입니다. 체험·만료 구독은 제외합니다.',
    refundRate: '선택 기간의 환불액을 총 수납액(개인 결제 수납액과 조직 청구 수납액의 합계)으로 나눈 비율입니다. 총 수납액이 0원이면 0%로 표시합니다.',
}


export default function BillingStatisticsPage() {
    const [periodPreset, setPeriodPreset] = useState('THIS_MONTH')
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')

    useEffect(() => {
        let active = true
        setLoading(true)
        setError('')
        api.get('/billing/admin/dashboard', { params: { period: periodPreset } })
            .then((response) => {
                if (active) setData(response?.data?.data ?? null)
            })
            .catch(() => {
                if (active) setError('과금 통계를 불러오지 못했습니다.')
            })
            .finally(() => {
                if (active) setLoading(false)
            })
        return () => {
            active = false
        }
    }, [periodPreset])

    const summary = data?.summary || {}
    const period = data?.period || {}
    const comparison = data?.periodComparison || {}
    const current = comparison.current || {}
    const previous = comparison.previous || {}
    const trend = Array.isArray(data?.trend) ? data.trend : []
    const planMix = Array.isArray(data?.planMix) ? data.planMix : []
    const channelMix = Array.isArray(data?.channelMix) ? data.channelMix : []
    const organization = data?.organizationBilling || {}

    const subscriberTrend = trend.map((item) => ({
        ...item,
        label: compactDate(item.date),
    }))

    return (
        <section>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <h1 className="text-2xl font-bold">과금통계</h1>
                    <MenuGuideButton slug="subscriptions" title="구독·결제 가이드" anchor="step-01" />
                </div>
                <div className="text-xs text-slate-500">업데이트 {formatDateTimeMinute(data?.generatedAt) || '-'}</div>
            </div>

            <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-3">
                <div className="inline-flex flex-wrap rounded border border-slate-200 bg-slate-50 p-1" role="group" aria-label="과금 통계 기간">
                    {PERIOD_OPTIONS.map((option) => (
                        <button
                            key={option.value}
                            type="button"
                            className={`rounded px-3 py-1.5 text-xs font-medium ${periodPreset === option.value ? 'bg-brand-navy text-white shadow-sm' : 'text-slate-600 hover:bg-white hover:text-slate-950'}`}
                            aria-pressed={periodPreset === option.value}
                            onClick={() => setPeriodPreset(option.value)}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
                <div className="text-xs text-slate-500">{periodRange(period)}</div>
            </div>

            {loading && <div className="py-16 text-center text-sm text-slate-500">통계를 불러오는 중...</div>}
            {!loading && error && <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

            {!loading && !error && (
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-2 lg:grid-cols-3 xl:grid-cols-6">
                        <Metric label="예상 MRR" value={currency(summary.estimatedMrr)} change={summary.mrrChangeRate} help={METRIC_HELP.estimatedMrr} />
                        <Metric label="예상 ARR" value={currency(summary.estimatedArr)} help={METRIC_HELP.estimatedArr} />
                        <Metric label="ARPA" value={currency(summary.arpa)} help={METRIC_HELP.arpa} />
                        <Metric label={`${period.label || '선택 기간'} 순수납`} value={currency(summary.collectedAmount)} change={summary.collectedAmountChangeRate} help={METRIC_HELP.collectedAmount} />
                        <Metric label="활성 과금 계정" value={`${number(summary.activeRevenueAccounts)}개`} meta={`개인 ${number(summary.activePaidSubscriptions)} · 조직 ${number(summary.activeOrgContracts)}`} help={METRIC_HELP.activeRevenueAccounts} />
                        <Metric label="환불률" value={percent(summary.refundRate)} alert={Number(summary.refundRate || 0) > 3} help={METRIC_HELP.refundRate} />
                    </div>

                    <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                        <Panel title="매출·수납 추세" meta={periodRange(period)} className="xl:col-span-2">
                            <div className="h-72 min-w-0">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={subscriberTrend} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                        <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={22} />
                                        <YAxis tickFormatter={compactCurrency} width={58} tick={{ fontSize: 11 }} />
                                        <Tooltip formatter={(value, name) => [currency(value), cashflowLabel(name)]} />
                                        <Legend formatter={cashflowLabel} wrapperStyle={{ fontSize: 12 }} />
                                        <Line type="monotone" dataKey="collectedAmount" stroke="#0f766e" strokeWidth={3} dot={false} />
                                        <Line type="monotone" dataKey="iapAmount" stroke="#2563eb" strokeWidth={2} dot={false} />
                                        <Line type="monotone" dataKey="orgPaidAmount" stroke="#d97706" strokeWidth={2} dot={false} />
                                        <Line type="monotone" dataKey="refundAmount" stroke="#e11d48" strokeWidth={2} dot={false} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </Panel>

                        <Panel title="기간 비교" meta={period.comparisonLabel || '직전 기간'}>
                            <ComparisonRow label="순수납" current={currency(current.collectedAmount)} previous={currency(previous.collectedAmount)} change={comparison.collectedAmountChangeRate} />
                            <ComparisonRow label="신규 구독" current={`${number(current.newPaidSubscriptions)}명`} previous={`${number(previous.newPaidSubscriptions)}명`} change={comparison.newPaidChangeRate} />
                            <ComparisonRow label="구독 이탈" current={`${number(current.churnedSubscriptions)}명`} previous={`${number(previous.churnedSubscriptions)}명`} change={comparison.churnedChangeRate} inverse />
                            <div className="mt-3 border-t border-slate-100 pt-3">
                                <Progress label="비교 기간 달성률" value={comparison.comparisonAttainmentRate} />
                                <Progress label="기간 진행률" value={comparison.progressRate} />
                            </div>
                        </Panel>
                    </div>

                    <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                        <Panel title="구독 유입·이탈" meta={period.label || '선택 기간'} className="xl:col-span-2">
                            <div className="h-60 min-w-0">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={subscriberTrend} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                        <XAxis dataKey="label" tick={{ fontSize: 10 }} minTickGap={20} />
                                        <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                                        <Tooltip />
                                        <Legend wrapperStyle={{ fontSize: 12 }} />
                                        <Bar dataKey="newPaidSubscriptions" name="신규" fill="#0284c7" radius={[2, 2, 0, 0]} />
                                        <Bar dataKey="churnedSubscriptions" name="이탈" fill="#e11d48" radius={[2, 2, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </Panel>

                        <Panel title="구독 품질">
                            <StatRow label="이탈률" value={percent(summary.churnRate)} alert={Number(summary.churnRate || 0) > 5} />
                            <StatRow label="결제 성공률" value={percent(summary.paymentSuccessRate)} alert={Number(summary.failedPayments || 0) > 0} />
                            <StatRow label="체험 전환율" value={percent(summary.trialConversionRate)} />
                            <StatRow label="환불액" value={currency(summary.refundedAmount)} />
                            <StatRow label="기간 순증" value={signed(current.netSubscriptionChange)} />
                        </Panel>
                    </div>

                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                        <Panel title="플랜 구성">
                            <MixRows items={planMix.map((item) => ({
                                key: item.segmentCode,
                                label: item.displayName || item.planName || item.planCode,
                                value: `${number(item.count)}${item.countUnit || '명'}`,
                                subvalue: currency(item.monthlyAmount),
                            }))} />
                        </Panel>

                        <Panel title="판매 채널">
                            <MixRows items={channelMix.map((item) => ({
                                key: item.channel,
                                label: channelLabel(item.channel),
                                value: `${number(item.count)}${item.countUnit || '명'}`,
                            }))} />
                        </Panel>

                        <Panel title="조직 청구 건전성">
                            <StatRow label="월 계약 예상액" value={currency(organization.monthlyContractAmount)} />
                            <StatRow label="발행 청구" value={currency(organization.issuedAmount)} />
                            <StatRow label="수납" value={currency(organization.paidAmount)} />
                            <StatRow label="현재 미수금" value={currency(organization.outstandingAmount)} alert={Number(organization.outstandingAmount || 0) > 0} />
                            <StatRow label="현재 연체" value={`${number(organization.overdueCount)}건 · ${currency(organization.overdueAmount)}`} alert={Number(organization.overdueCount || 0) > 0} />
                            <div className="mt-3 border-t border-slate-100 pt-3">
                                <Progress label="청구 회수율" value={organization.collectionRate} />
                            </div>
                        </Panel>
                    </div>
                </div>
            )}
        </section>
    )
}

function Metric({ label, value, change, meta, alert = false, help = '' }) {
    const hasChange = change !== undefined && change !== null
    const changeNumber = Number(change || 0)
    return (
        <div className={`min-h-24 rounded border bg-white p-3 ${alert ? 'border-rose-200' : 'border-slate-200'}`}>
            <div className="flex min-w-0 items-center gap-1 text-xs font-medium text-slate-500">
                <span className="truncate">{label}</span>
                {help && <MetricHelp label={label} text={help} />}
            </div>
            <div className={`mt-1 truncate text-lg font-semibold ${alert ? 'text-rose-700' : 'text-slate-950'}`}>{value}</div>
            <div className="mt-1 min-h-4 truncate text-xs text-slate-500">
                {hasChange ? <span className={changeNumber >= 0 ? 'text-emerald-700' : 'text-rose-700'}>{changeNumber > 0 ? '+' : ''}{changeNumber.toFixed(1)}%</span> : meta}
            </div>
        </div>
    )
}
function MetricHelp({ label, text }) {
    return (
        <span className="group relative inline-flex shrink-0">
            <button type="button" className="inline-flex h-4 w-4 items-center justify-center rounded text-slate-400 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-500" aria-label={`${label} 계산 기준: ${text}`}>
                <CircleHelp className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
            <span role="tooltip" className="pointer-events-none absolute left-0 top-full z-30 mt-2 hidden w-64 max-w-[calc(100vw-2rem)] rounded border border-slate-200 bg-white p-2 text-xs font-normal leading-relaxed text-slate-700 shadow-lg group-hover:block group-focus-within:block">
                {text}
            </span>
        </span>
    )
}

function Panel({ title, meta, className = '', children }) {
    return (
        <section className={`rounded border border-slate-200 bg-white p-4 ${className}`}>
            <div className="mb-3 flex items-center justify-between gap-3 border-b border-slate-100 pb-2">
                <h2 className="text-sm font-semibold text-slate-950">{title}</h2>
                {meta && <span className="truncate text-xs text-slate-500">{meta}</span>}
            </div>
            {children}
        </section>
    )
}

function ComparisonRow({ label, current, previous, change, inverse = false }) {
    const numberValue = Number(change || 0)
    const favorable = inverse ? numberValue <= 0 : numberValue >= 0
    return (
        <div className="border-b border-slate-100 py-3 last:border-b-0">
            <div className="flex items-center justify-between gap-3">
                <span className="text-xs text-slate-500">{label}</span>
                <strong className="text-sm text-slate-900">{current}</strong>
            </div>
            <div className="mt-1 flex items-center justify-between gap-3 text-xs">
                <span className="text-slate-400">비교 {previous}</span>
                <span className={favorable ? 'text-emerald-700' : 'text-rose-700'}>{numberValue > 0 ? '+' : ''}{numberValue.toFixed(1)}%</span>
            </div>
        </div>
    )
}

function Progress({ label, value }) {
    const normalized = Math.max(0, Math.min(100, Number(value || 0)))
    return (
        <div className="mb-3 last:mb-0">
            <div className="mb-1 flex items-center justify-between text-xs text-slate-500">
                <span>{label}</span>
                <strong className="text-slate-800">{percent(value)}</strong>
            </div>
            <div className="h-1.5 overflow-hidden rounded bg-slate-100">
                <div className="h-full bg-teal-600" style={{ width: `${normalized}%` }} />
            </div>
        </div>
    )
}

function StatRow({ label, value, alert = false }) {
    return (
        <div className="flex items-center justify-between gap-3 border-b border-slate-100 py-2.5 last:border-b-0">
            <span className="text-xs text-slate-500">{label}</span>
            <strong className={`text-sm ${alert ? 'text-rose-700' : 'text-slate-900'}`}>{value}</strong>
        </div>
    )
}

function MixRows({ items }) {
    if (!items.length) return <div className="py-8 text-center text-sm text-slate-500">집계 데이터가 없습니다.</div>
    return (
        <div>
            {items.map((item) => (
                <div key={item.key} className="flex items-center justify-between gap-3 border-b border-slate-100 py-2.5 last:border-b-0">
                    <span className="min-w-0 truncate text-sm text-slate-700">{item.label}</span>
                    <span className="shrink-0 text-right">
                        <strong className="block text-sm text-slate-900">{item.value}</strong>
                        {item.subvalue && <span className="text-xs text-slate-400">{item.subvalue}</span>}
                    </span>
                </div>
            ))}
        </div>
    )
}

function periodRange(period) {
    const from = formatDate(period?.from)
    const to = formatDate(period?.to)
    return from && to ? `${period?.label || '선택 기간'} · ${from}~${to}` : '한국시간 기준'
}

function compactDate(value) {
    const text = String(value || '')
    return text.length >= 10 ? text.slice(5).replace('-', '.') : text
}

function compactCurrency(value) {
    const amount = Number(value || 0)
    if (Math.abs(amount) >= 100000000) return `${(amount / 100000000).toFixed(1)}억`
    if (Math.abs(amount) >= 10000) return `${Math.round(amount / 10000)}만`
    return amount.toLocaleString()
}

function cashflowLabel(value) {
    return {
        collectedAmount: '순수납',
        iapAmount: '개인 결제',
        orgPaidAmount: '조직 수납',
        refundAmount: '환불',
    }[value] || value
}

function channelLabel(value) {
    return {
        APP_STORE: 'App Store',
        PLAY_STORE: 'Play Store',
        WEB_PG: '웹 PG',
        WEB_PG_KCP: '웹 PG · NHN KCP',
        WEB_PG_KPN: '웹 PG · 한국결제네트웍스',
        ORG_CONTRACT: '조직 계약',
        MANUAL: '운영자·기타',
    }[value] || value || '-'
}

function currency(value) {
    const amount = Number(value || 0)
    return `₩${amount.toLocaleString()}`
}

function number(value) {
    return Number(value || 0).toLocaleString()
}

function percent(value) {
    return `${Number(value || 0).toFixed(1)}%`
}

function signed(value) {
    const amount = Number(value || 0)
    return `${amount > 0 ? '+' : ''}${amount.toLocaleString()}명`
}
